import { LitElement, css, html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

export type SpotlightStep = {
  id: string;
  title: string;
  text: string;
  progress: string;
  target: Element | null;
  keepClear?: Element | null;
};

type Placement = { x: number; y: number; side: "top" | "bottom" | "left" | "right" | "center"; arrow: number };

@customElement("spotlight-tour")
export class SpotlightTour extends LitElement {
  @property({ attribute: false }) resolveStep: () => SpotlightStep | null = () => null;
  @property({ attribute: false }) controls: TemplateResult | "" = "";
  @property({ attribute: false }) dismiss: () => void = () => {};
  @property() label = "Guided tour";
  @property({ type: Boolean }) walkthrough = false;
  @property({ type: Boolean }) collapsed = false;
  @state() private step: SpotlightStep | null = null;
  @state() private spotlight: { x: number; y: number; width: number; height: number } | null = null;
  @state() private placement: Placement = { x: 12, y: 12, side: "center", arrow: 0 };
  private frame = 0;
  private scrolledStep = "";
  private focused = false;
  private previousFocus: HTMLElement | null = null;

  static styles = css`
    :host { position: fixed; inset: 0; z-index: 1000; pointer-events: none; font-family: var(--font-ui, system-ui); color: var(--text); font-size: 14px; }
    .spotlight { position: fixed; box-sizing: border-box; border: 2px solid var(--lavender); border-radius: 8px; box-shadow: 0 0 0 100vmax rgb(10 8 14 / .7); pointer-events: none; }
    .tooltip { position: fixed; box-sizing: border-box; width: min(336px, calc(100vw - 24px)); max-height: calc(100dvh - 24px); overflow: auto; padding: 18px; border: 1px solid var(--border-strong); border-radius: 8px; background: var(--surface); box-shadow: 0 8px 32px rgb(10 8 14 / .4); pointer-events: auto; line-height: 1.5; }
    .progress { color: var(--lavender); font-size: 12px; font-weight: 600; }
    h2 { margin: 6px 0 8px; font-size: 18px; line-height: 1.3; color: var(--text-strong); }
    p { margin: 0 0 16px; color: var(--text); }
    .controls { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
    button { font: inherit; font-size: 13px; font-weight: 600; border: 1px solid var(--brand-hi); border-radius: 4px; padding: 7px 12px; cursor: pointer; background: var(--brand); color: var(--text-strong); }
    button:hover { background: var(--brand-hi); }
    button.secondary { background: var(--surface-2); border-color: var(--border-strong); color: var(--text); }
    button.secondary:hover { background: var(--surface-3); }
    button:focus-visible { outline: 2px solid var(--lavender); outline-offset: 3px; }
    .arrow { position: fixed; width: 10px; height: 10px; transform: rotate(45deg); background: var(--surface); border: 1px solid var(--border-strong); }
    .collapsed { padding: 12px; }
    .collapsed h2 { font-size: 14px; margin: 0 0 8px; }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    let active = document.activeElement;
    while (active?.shadowRoot?.activeElement) active = active.shadowRoot.activeElement;
    this.previousFocus = active instanceof HTMLElement ? active : null;
    window.addEventListener("keydown", this.onKeyDown, true);
    this.frame = requestAnimationFrame(this.trackTarget);
  }

  disconnectedCallback(): void {
    cancelAnimationFrame(this.frame);
    window.removeEventListener("keydown", this.onKeyDown, true);
    if (this.walkthrough && this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
    super.disconnectedCallback();
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.dismiss();
    } else if (this.shadowRoot?.activeElement && (event.key === " " || event.key === "Enter")) {
      event.stopImmediatePropagation();
    } else if (event.key === "Tab" && this.walkthrough && this.shadowRoot?.activeElement) {
      const buttons = [...this.renderRoot.querySelectorAll<HTMLButtonElement>("button")];
      const first = buttons[0], last = buttons[buttons.length - 1];
      if (event.shiftKey && this.shadowRoot.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && this.shadowRoot.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
  };

  private readonly trackTarget = (): void => {
    if (!this.isConnected) return;
    const next = this.resolveStep();
    if (next) {
      if (this.step?.id !== next.id || this.step.text !== next.text || this.step.target !== next.target) this.step = next;
      const target = next.target;
      if (target && !this.collapsed && this.walkthrough && this.scrolledStep !== next.id) {
        this.scrolledStep = next.id;
        const rect = target.getBoundingClientRect();
        if (rect.top < 12 || rect.bottom > innerHeight - 12) target.scrollIntoView({ block: "center", inline: "nearest", behavior: "instant" });
      }
      const rect = target?.getBoundingClientRect();
      const visible = rect && rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < innerHeight;
      const hole = visible && !this.collapsed ? {
        x: Math.max(4, rect.left - 6), y: Math.max(4, rect.top - 6),
        width: Math.max(0, Math.min(innerWidth - 4, rect.right + 6) - Math.max(4, rect.left - 6)),
        height: Math.max(0, Math.min(innerHeight - 4, rect.bottom + 6) - Math.max(4, rect.top - 6)),
      } : null;
      if (JSON.stringify(hole) !== JSON.stringify(this.spotlight)) this.spotlight = hole;
      const panel = this.renderRoot.querySelector<HTMLElement>(".tooltip");
      const width = panel?.offsetWidth || Math.min(336, innerWidth - 24);
      const height = panel?.offsetHeight || 220;
      const clamp = (position: number, size: number, viewport: number) => Math.max(12, Math.min(position, viewport - size - 12));
      let x = (innerWidth - width) / 2, y = (innerHeight - height) / 2;
      let side: Placement["side"] = "center", arrow = 0;
      if (this.collapsed) { x = innerWidth - width - 12; y = 12; }
      else if (hole) {
        x = clamp(hole.x + hole.width / 2 - width / 2, width, innerWidth);
        if (hole.y + hole.height + height + 24 <= innerHeight) { y = hole.y + hole.height + 14; side = "bottom"; }
        else if (hole.y >= height + 26) { y = hole.y - height - 14; side = "top"; }
        else if (hole.x >= width + 26) { x = hole.x - width - 14; y = clamp(hole.y, height, innerHeight); side = "left"; }
        else if (hole.x + hole.width + width + 26 <= innerWidth) { x = hole.x + hole.width + 14; y = clamp(hole.y, height, innerHeight); side = "right"; }
        else {
          const clear = next.keepClear?.getBoundingClientRect();
          const corners = [[innerWidth - width - 12, innerHeight - height - 12], [innerWidth - width - 12, 12], [12, innerHeight - height - 12], [12, 12]];
          [x, y] = corners.find(([cx, cy]) => !clear || cx >= clear.right || cx + width <= clear.left || cy >= clear.bottom || cy + height <= clear.top) ?? corners[0];
        }
        arrow = side === "top" || side === "bottom" ? Math.max(18, Math.min(width - 18, hole.x + hole.width / 2 - x))
          : Math.max(18, Math.min(height - 18, hole.y + hole.height / 2 - y));
      }
      const placed = { x: Math.round(x), y: Math.round(y), side, arrow: Math.round(arrow) };
      if (JSON.stringify(placed) !== JSON.stringify(this.placement)) this.placement = placed;
      if (this.walkthrough && !this.focused && panel) {
        this.focused = true;
        this.renderRoot.querySelector<HTMLButtonElement>("button[data-next]")?.focus({ preventScroll: true });
      }
    }
    this.frame = requestAnimationFrame(this.trackTarget);
  };

  render(): TemplateResult | typeof nothing {
    if (!this.step) return nothing;
    const hole = this.spotlight;
    const { x, y, side, arrow } = this.placement;
    const panel = this.renderRoot.querySelector<HTMLElement>(".tooltip");
    const arrowX = side === "top" || side === "bottom" ? x + arrow - 5 : side === "left" ? x + (panel?.offsetWidth || 336) - 5 : x - 5;
    const tooltipY = side === "top" && hole ? hole.y - 14 : y;
    const arrowY = side === "left" || side === "right" ? y + arrow - 5 : side === "top" ? tooltipY - 5 : y - 5;
    return html`
      ${hole ? html`<div class="spotlight" data-target=${this.step.id} style=${`left:${hole.x}px;top:${hole.y}px;width:${hole.width}px;height:${hole.height}px`} aria-hidden="true"></div>` : nothing}
      ${hole && side !== "center" ? html`<div class="arrow" style=${`left:${arrowX}px;top:${arrowY}px`} aria-hidden="true"></div>` : nothing}
      <section class="tooltip ${this.collapsed ? "collapsed" : ""}" role=${this.walkthrough ? "dialog" : "complementary"} aria-label=${this.label}
        data-step=${this.step.id} style=${`left:${x}px;top:${tooltipY}px;transform:${side === "top" ? "translateY(-100%)" : "none"}`}>
        ${this.collapsed ? html`<h2>${this.step.title}</h2>` : html`<div aria-live="polite"><span class="progress">${this.step.progress}</span><h2>${this.step.title}</h2><p>${this.step.text}</p></div>`}
        <div class="controls">${this.controls}</div>
      </section>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { "spotlight-tour": SpotlightTour; }
}
